import { STORAGE_KEY } from "../constants";
import type {
  DailyLog,
  GroomingItem,
  LearningItem,
  NutritionItem,
  Profile,
  RitualSnapshot,
  WardrobeItem,
} from "../types";
import { defaultGroomingItems, defaultProfile, emptySnapshot } from "./defaults";
import { isoNow, localDateKey } from "./date";
import { newId } from "./id";
import { defaultSchedule, defaultWindow, normalizeSchedule } from "./schedule";

type AnyRecord = Record<string, unknown>;

function asRecord(value: unknown): AnyRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as AnyRecord)
    : {};
}

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown) {
  return typeof value === "string" && value ? value : null;
}

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = true) {
  return typeof value === "boolean" ? value : fallback;
}

function asArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function withSyncFields<T extends AnyRecord>(
  input: AnyRecord,
  item: T,
  now: string,
): T & {
  id: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
} {
  return {
    ...item,
    id: asString(input.id, newId()),
    user_id: asNullableString(input.user_id),
    created_at: asString(input.created_at, now),
    updated_at: asString(input.updated_at, now),
    deleted_at: asNullableString(input.deleted_at),
  };
}

function migrateNutrition(value: unknown, now: string): NutritionItem {
  const input = asRecord(value);
  const today = localDateKey(new Date());
  const fallback = defaultSchedule("daily", today, {
    windows: [defaultWindow("Take", "13:00", "14:00")],
  });
  return withSyncFields(
    input,
    {
      name: asString(input.name, "Untitled item"),
      category: asString(input.category, "Other"),
      quantity: asString(input.quantity),
      schedule: normalizeSchedule(input.schedule, fallback),
    },
    now,
  );
}

function migrateGrooming(value: unknown, now: string): GroomingItem {
  const input = asRecord(value);
  const intervalDays = Math.max(1, asNumber(input.interval_days ?? input.intervalDays, 1));
  const today = localDateKey(new Date());
  const fallback =
    intervalDays === 1
      ? defaultSchedule("daily", today, { windows: [defaultWindow("Morning", "08:00", "10:00")] })
      : defaultSchedule("interval", today, {
          interval_days: intervalDays,
          windows: [defaultWindow("Morning", "08:00", "10:00")],
        });
  return withSyncFields(
    input,
    {
      name: asString(input.name, "Grooming habit"),
      interval_days: intervalDays,
      last_done: asNullableString(input.last_done ?? input.lastDone),
      schedule: normalizeSchedule(input.schedule, fallback),
    },
    now,
  );
}

function migrateWardrobe(value: unknown, now: string, timezone: string): WardrobeItem {
  const input = asRecord(value);
  const today = localDateKey(new Date(), timezone);
  const fallback = defaultSchedule("weekly", today, {
    weekdays: [weekdayForLocalDate(today)],
    windows: [defaultWindow("Wear", "08:00", "11:00")],
  });
  return withSyncFields(
    input,
    {
      name: asString(input.name, "Wardrobe item"),
      category: asString(input.category, "Other"),
      color: asString(input.color),
      notes: asString(input.notes),
      image_url: asString(input.image_url),
      last_worn: asNullableString(input.last_worn ?? input.lastWorn),
      added_on: asString(input.added_on ?? input.addedOn, today),
      schedule: normalizeSchedule(input.schedule, fallback),
    },
    now,
  );
}

function migrateLearning(value: unknown, now: string, timezone: string): LearningItem {
  const input = asRecord(value);
  const today = localDateKey(new Date(), timezone);
  const fallback = defaultSchedule("daily", today, {
    windows: [defaultWindow("Learn", "20:00", "21:00")],
  });
  return withSyncFields(
    input,
    {
      title: asString(input.title, "Learning item"),
      type: asString(input.type, "Other"),
      category: asString(input.category, "Other"),
      progress: Math.min(100, Math.max(0, asNumber(input.progress, 0))),
      notes: asString(input.notes),
      active: asBoolean(input.active, true),
      started_on: asString(input.started_on ?? input.startedOn, today),
      schedule: normalizeSchedule(input.schedule, fallback),
    },
    now,
  );
}

function weekdayForLocalDate(dateKey: string) {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function migrateProfile(value: unknown, now: string): Profile {
  const fallback = defaultProfile(now);
  const input = asRecord(value);

  return {
    id: asString(input.id, fallback.id),
    display_name: asString(input.display_name ?? input.displayName, fallback.display_name),
    timezone: asString(input.timezone, fallback.timezone),
    created_at: asString(input.created_at, now),
    updated_at: asString(input.updated_at, now),
  };
}

function migrateLogs(value: unknown, now: string): DailyLog[] {
  if (Array.isArray(value)) {
    return value.map((entry) => {
      const input = asRecord(entry);
      const logDate = asString(input.log_date);
      const category = asString(input.category) as DailyLog["category"];
      const itemId = asString(input.item_id);
      return {
        id: asString(input.id, `${logDate}_${category}_${itemId}`),
        user_id: asNullableString(input.user_id),
        log_date: logDate,
        category,
        item_id: itemId,
        occurrence_id: asString(input.occurrence_id, "default"),
        created_at: asString(input.created_at, now),
      };
    });
  }

  const record = asRecord(value);
  return Object.entries(record)
    .filter(([, done]) => done)
    .map(([key]) => {
      const [logDate, category, ...itemParts] = key.split("_");
      const itemId = itemParts.join("_");
      return {
        id: `${logDate}_${category}_${itemId}`,
        user_id: null,
        log_date: logDate,
        category: category as DailyLog["category"],
        item_id: itemId,
        occurrence_id: "default",
        created_at: now,
      };
    })
    .filter((log) => log.log_date && log.category && log.item_id);
}

export function snapshotFromUnknown(value: unknown, now = isoNow()): RitualSnapshot {
  const wrapper = asRecord(value);
  const source = "snapshot" in wrapper ? asRecord(wrapper.snapshot) : wrapper;
  const profile = migrateProfile(source.profile, now);

  const snapshot: RitualSnapshot = {
    profile,
    nutrition: asArray(source.nutrition).map((item) => migrateNutrition(item, now)),
    grooming: asArray(source.grooming).map((item) => migrateGrooming(item, now)),
    wardrobe: asArray(source.wardrobe).map((item) => migrateWardrobe(item, now, profile.timezone)),
    learning: asArray(source.learning).map((item) => migrateLearning(item, now, profile.timezone)),
    logs: migrateLogs(source.logs, now),
    last_synced_at: asNullableString(source.last_synced_at ?? source.lastSyncedAt),
  };

  if (snapshot.grooming.filter((item) => !item.deleted_at).length === 0) {
    snapshot.grooming = defaultGroomingItems(now);
  }

  return snapshot;
}

export function loadLegacyLocalStorage(): RitualSnapshot | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    return snapshotFromUnknown(JSON.parse(raw));
  } catch {
    return emptySnapshot();
  }
}
